/**
 * SplitBar
 * Classification: custom
 *
 * A single proportional bar split into labelled segments — the compact
 * "12 critical / 30 high / 8 low" summary used in tables and cards.
 *
 * Segment counts are always rendered as text. Severity `medium` and `low` sit in
 * the contrast relief band against a light surface, which obliges a visible
 * label rather than colour alone.
 */
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { chartTokens } from '@/tokens/chart';
import { fillInk, severityColorsByMode, type SeverityKey } from '@/tokens/colors';

export interface SplitBarSegment {
  key: SeverityKey | string;
  value: number;
  /** Overrides the scale colour. Use sparingly. */
  color?: string;
}

export interface SplitBarProps {
  segments: readonly SplitBarSegment[];
  /** `severity` colours by severity key; `series` colours by slot order. */
  scale?: 'severity' | 'series';
  /** Accessible name for the whole bar, e.g. "Findings by severity". */
  label: string;
  height?: number;
  /** Hides the per-segment counts. Only legal when values appear adjacent. */
  hideValues?: boolean;
}

export function SplitBar({
  segments,
  scale = 'severity',
  label,
  height = 20,
  hideValues = false,
}: SplitBarProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const tokens = chartTokens[mode];
  const severity = severityColorsByMode[mode];

  const visible = segments.filter((segment) => segment.value > 0);
  const total = visible.reduce((sum, segment) => sum + segment.value, 0);
  if (total <= 0) return null;

  const colorFor = (segment: SplitBarSegment, index: number): string => {
    if (segment.color) return segment.color;
    if (scale === 'severity' && segment.key in severity) {
      return severity[segment.key as SeverityKey];
    }
    return tokens.series[index % tokens.series.length];
  };

  return (
    <Box
      role="img"
      aria-label={`${label}: ${visible.map((s) => `${s.value} ${s.key}`).join(', ')}`}
      sx={{
        display: 'flex',
        width: '100%',
        height,
        borderRadius: 0.5,
        overflow: 'hidden',
        // The 2px surface gap between adjacent fills is the mandated secondary
        // encoding that makes an adjacent CVD separation in the relief band legal.
        gap: '2px',
      }}
    >
      {visible.map((segment, index) => (
        <Box
          key={segment.key}
          sx={{
            flexGrow: segment.value,
            flexBasis: 0,
            minWidth: 0,
            backgroundColor: colorFor(segment, index),
            display: 'grid',
            placeItems: 'center',
            fontSize: Math.max(10, height * 0.55),
            fontWeight: 600,
            // Text sits on a saturated fill, so it takes a fixed high-contrast
            // ink rather than a theme token that could invert underneath it.
            color: mode === 'dark' ? fillInk.onLight : fillInk.onDark,
            overflow: 'hidden',
          }}
        >
          {!hideValues && segment.value}
        </Box>
      ))}
    </Box>
  );
}
